import type { CatalogIngredient } from './api/catalog.ts';
import type { IngredientLine, RecipeSummary } from './api/recipes.ts';
import type { SyncRecipe } from './api/sync.ts';
import type { CatalogFacts } from './catalog.ts';
import { recipeFigures, type CalcLine } from './nutrition.ts';

/** The calculator's view of a catalog entry, with the household's effective prices. */
export function catalogFacts(entry: CatalogIngredient): CatalogFacts {
  return {
    id: entry.id,
    nutrients: entry.nutrients,
    unitGrams: entry.unitGrams,
    densityGPerMl: entry.densityGPerMl,
    packSize: entry.packSize,
    priceEurPerKg: entry.household.priceEurPerKg,
    priceEurPerPiece: entry.household.priceEurPerPiece,
    shelfDays: entry.shelfDays,
    verified: entry.verified,
  };
}

export function calcLines(
  lines: Pick<
    IngredientLine,
    'id' | 'name' | 'quantity' | 'quantityMax' | 'unit' | 'optional' | 'grams' | 'ingredientId'
  >[],
  catalog: Map<string, CatalogIngredient>,
): CalcLine[] {
  return lines.map((line) => {
    const entry = line.ingredientId ? catalog.get(line.ingredientId) : undefined;
    return {
      id: line.id,
      name: line.name,
      quantity: line.quantity,
      quantityMax: line.quantityMax,
      unit: line.unit,
      optional: line.optional,
      grams: line.grams,
      ingredient: entry ? catalogFacts(entry) : null,
    };
  });
}

/** The list figures of a recipe: kcal, protein and cost per portion. */
export function summaryFigures(
  lines: Parameters<typeof calcLines>[0],
  catalog: Map<string, CatalogIngredient>,
  yieldPortions: number,
) {
  const figures = recipeFigures(calcLines(lines, catalog), yieldPortions);
  const counted = figures.coverage.counted > 0;
  return {
    kcalPerPortion: counted ? figures.perPortion.kcal : null,
    proteinPerPortion: counted ? figures.perPortion.protein : null,
    costPerPortion: figures.cost.perPortion,
    nutritionEstimate: figures.estimate,
  };
}

/** A list card from a mirrored recipe (the offline list; the server builds the same). */
export function summarizeRecipe(
  recipe: SyncRecipe,
  lines: Parameters<typeof calcLines>[0],
  catalog: Map<string, CatalogIngredient>,
): RecipeSummary {
  return {
    id: recipe.id,
    title: recipe.title,
    yieldPortions: recipe.yieldPortions,
    totalMin: recipe.totalMin,
    prepMin: recipe.prepMin,
    cookMin: recipe.cookMin,
    sourceType: recipe.sourceType,
    imageId: recipe.imageId,
    tags: recipe.tags,
    mealTypes: recipe.mealTypes,
    diets: recipe.diets,
    mealPrepFriendly: recipe.mealPrepFriendly,
    freezerFriendly: recipe.freezerFriendly,
    rating: recipe.rating,
    favourite: recipe.favourite,
    reviewedAt: recipe.reviewedAt,
    lastCookedOn: recipe.lastCookedOn,
    createdAt: recipe.createdAt,
    translation: recipe.translation
      ? {
          language: recipe.translation.language,
          title: recipe.translation.title,
          tags: recipe.translation.tags,
        }
      : null,
    ...summaryFigures(lines, catalog, recipe.yieldPortions),
  };
}
